import { comparerByDate } from '@app/comparers';
import { Member } from '@app/models';
import {
  MemberActionsUnion,
  MemberActionTypes
} from '@core/actions/member.actions';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeatureSelector, createSelector } from '@ngrx/store';

export interface State extends EntityState<Member> {
  loading: boolean;
  creating: boolean;
  removing: boolean;
  loaded: boolean;
}

export const adapter: EntityAdapter<Member> = createEntityAdapter<Member>({
  selectId: (member: Member) => member.id,
  sortComparer: comparerByDate('DESC', 'createdAt')
});

export const INITIAL_STATE: State = adapter.getInitialState({
  loading: false,
  creating: false,
  removing: false,
  loaded: false
});

export function reducer(
  state: State = INITIAL_STATE,
  action: MemberActionsUnion
) {
  switch (action.type) {
    case MemberActionTypes.Load:
    case MemberActionTypes.LoadMe:
      return { ...state, loading: true };

    case MemberActionTypes.LoadFail:
    case MemberActionTypes.LoadMeFail:
      return { ...state, loading: false };

    case MemberActionTypes.LoadSuccess:
    case MemberActionTypes.LoadMeSuccess:
      return adapter.addAll(action.members, {
        ...state,
        loading: false,
        loaded: true
      });

    case MemberActionTypes.Create:
      return { ...state, creating: true };

    case MemberActionTypes.CreateFail:
      return { ...state, creating: false };

    case MemberActionTypes.CreateSuccess:
      return adapter.upsertOne(action.member, {
        ...state,
        creating: false
      });

    case MemberActionTypes.UpdateSuccess:
      return adapter.upsertOne(action.member, state);

    case MemberActionTypes.Remove:
      return { ...state, removing: true };

    case MemberActionTypes.RemoveFail:
      return { ...state, removing: false };

    case MemberActionTypes.RemoveSuccess:
      return adapter.removeOne(action.id, {
        ...state,
        removing: false
      });

    default:
      return state;
  }
}

const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal
} = adapter.getSelectors();

const selectLoaded = (state: State) => state.loaded;
const selectLoading = (state: State) => state.loading;

export const getMembersState = createFeatureSelector<State>('members');

export const getMembers = createSelector(
  getMembersState,
  selectAll
);

export const getMembersLoaded = createSelector(
  getMembersState,
  selectLoaded
);

export const getMembersLoading = createSelector(
  getMembersState,
  selectLoading
);

export const getMemberEntities = createSelector(
  getMembersState,
  selectEntities
);
